// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Single upload slot row — shows slot label, uploaded file info, and upload/replace/delete actions.
 * Used by SlotUploadGrid and directly by consumers needing per-slot upload.
 */
import { useCallback, useRef, useState, type FC, type ChangeEvent } from 'react'
import { FileText, Plus, Loader2, Trash2, Upload, CheckCircle2 } from 'lucide-react'
import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
} from '@/components/ui'
import type { UploadSlot } from '../types'

/** Minimal document info needed to display a filled slot */
export interface SlotDocument {
  documentId: string
  latestFilename: string
  latestSizeBytes: number
}

interface SlotUploadCardProps {
  slot: UploadSlot
  document: SlotDocument | null
  onUpload: (file: File, slotId: string) => Promise<void>
  onDelete?: (documentId: string) => Promise<void>
  disabled?: boolean
  /** Compact inline style (SRM workspace) vs card style (portal) */
  variant?: 'inline' | 'card'
  /** Format bytes for display — consumers provide their own formatter */
  formatSize?: (bytes: number) => string
}

const defaultFormatSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const SlotUploadCard: FC<SlotUploadCardProps> = ({
  slot,
  document,
  onUpload,
  onDelete,
  disabled = false,
  variant = 'inline',
  formatSize = defaultFormatSize,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleFileSelect = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setIsUploading(true)
    try {
      await onUpload(file, slot.id)
    } finally {
      setIsUploading(false)
    }
  }, [onUpload, slot.id])

  const handleRemove = useCallback(async () => {
    if (!document || !onDelete) return
    setIsDeleting(true)
    try {
      await onDelete(document.documentId)
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
    }
  }, [document, onDelete])

  const triggerFileSelect = () => fileInputRef.current?.click()
  const isBusy = isUploading || isDeleting || disabled

  if (variant === 'card') {
    return (
      <div className="rounded-md border p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {document ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            ) : (
              <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium">{slot.label}</p>
              {document && (
                <p className="text-xs text-muted-foreground truncate" data-testid={`file-chip-${document.latestFilename}`}>
                  {document.latestFilename} · {formatSize(document.latestSizeBytes)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {document && onDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isBusy}
                aria-label={`Delete ${slot.label}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant={document ? 'outline' : 'default'}
              size="sm"
              onClick={triggerFileSelect}
              disabled={isBusy}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {document ? 'Replace' : 'Upload'}
            </Button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx"
          data-testid={`file-input-${slot.id}`}
          onChange={e => void handleFileSelect(e)}
        />
        {document && onDelete && (
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove document?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove &ldquo;{document.latestFilename}&rdquo; from this request.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handleRemove()} disabled={isDeleting}>
                  {isDeleting ? 'Removing...' : 'Remove'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    )
  }

  // Inline variant (compact row)
  if (document) {
    return (
      <div className="flex items-center gap-3 px-3 py-2">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium w-48 shrink-0">{slot.label}</span>
        <span className="text-sm truncate flex-1" title={document.latestFilename} data-testid={`file-chip-${document.latestFilename}`}>
          {document.latestFilename}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatSize(document.latestSizeBytes)}
        </span>
        <div className="flex gap-1 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={triggerFileSelect}
                disabled={isBusy}
              >
                {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </TooltipTrigger>
            <DarkTooltipContent>Replace</DarkTooltipContent>
          </Tooltip>
          {onDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isBusy}
                >
                  {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <DarkTooltipContent>Remove</DarkTooltipContent>
            </Tooltip>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx"
          data-testid={`file-input-${slot.id}`}
          onChange={e => void handleFileSelect(e)}
        />
        {onDelete && (
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove document?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove &ldquo;{document.latestFilename}&rdquo; from this request.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handleRemove()} disabled={isDeleting}>
                  {isDeleting ? 'Removing...' : 'Remove'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    )
  }

  // Empty inline slot
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="h-4 w-4 shrink-0" />
      <span className="text-sm text-muted-foreground w-48 shrink-0">{slot.label}</span>
      <span className="text-xs text-muted-foreground flex-1">&mdash;</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={triggerFileSelect}
            disabled={isBusy}
          >
            {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          </Button>
        </TooltipTrigger>
        <DarkTooltipContent>Add document</DarkTooltipContent>
      </Tooltip>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx"
        data-testid={`file-input-${slot.id}`}
        onChange={e => void handleFileSelect(e)}
      />
    </div>
  )
}
